import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { retryPendingRazorpayVerification } from '../utils/razorpayPendingVerify.js';

const AuthContext = createContext(null);

function schedulePendingPaymentRetry() {
  retryPendingRazorpayVerification().catch(() => {});
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      // No token, user is not logged in - set loading to false immediately
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await authAPI.getMe();
      const userData = response.data.data?.user || response.data.user;
      setUser(userData);
      schedulePendingPaymentRetry();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      
      // Check if response indicates failure
      if (!response.data.success) {
        return {
          success: false,
          message: response.data.message || 'Login failed'
        };
      }
      
      const { user, token } = response.data.data || response.data;
      
      if (!user || !token) {
        return {
          success: false,
          message: 'Invalid response from server'
        };
      }
      
      setUser(user);
      setToken(token);
      localStorage.setItem('token', token);
      schedulePendingPaymentRetry();
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const sendOtp = async (phone) => {
    try {
      const response = await authAPI.sendOtp(phone);
      if (!response.data.success) {
        return { success: false, message: response.data.message || 'Failed to send OTP' };
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to send OTP'
      };
    }
  };

  const loginWithOtp = async (phone, otp, { firstName, lastName } = {}) => {
    try {
      const response = await authAPI.verifyOtp(phone, otp, { firstName, lastName });
      if (!response.data.success) {
        return {
          success: false,
          message: response.data.message || 'Verification failed'
        };
      }
      // needsRegister is on response.data (top level), not inside data
      if (response.data.needsRegister) {
        const phoneFromServer = response.data.data?.phone || phone;
        return { success: true, needsRegister: true, phone: phoneFromServer };
      }
      const data = response.data.data || response.data;
      const { user, token } = data;
      if (!user || !token) {
        return { success: false, message: 'Invalid response from server' };
      }
      setUser(user);
      setToken(token);
      localStorage.setItem('token', token);
      schedulePendingPaymentRetry();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Verification failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      
      if (!response.data.success) {
        return {
          success: false,
          message: response.data.message || 'Registration failed'
        };
      }
      
      const { user, token } = response.data.data || response.data;
      
      if (!user || !token) {
        return {
          success: false,
          message: 'Invalid response from server'
        };
      }
      
      setUser(user);
      setToken(token);
      localStorage.setItem('token', token);
      schedulePendingPaymentRetry();
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Registration failed'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    sessionStorage.removeItem('postLoginRedirectToProfile');
  };

  const value = {
    user,
    token,
    loading,
    refreshUser: fetchUser,
    login,
    register,
    sendOtp,
    loginWithOtp,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
